import { useState, useMemo, useEffect } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { RequirePermission } from "@/components/RequirePermission";
import { usePermissions } from "@/hooks/usePermissions";
import { useProjects, useDeleteProject, type ProjectStatusFilter } from "@/hooks/useProjects";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { NewProjectWizard } from "@/components/controller/NewProjectWizard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/shared/DataTable";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, FileSpreadsheet, FileText, Printer } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { PageActions } from "@/components/shared/PageActions";
import { useExportActions } from "@/lib/export";
import { type ColumnDef } from "@tanstack/react-table";

const locationStr = (p: { city?: string | null; state?: string | null }) => {
  const parts = [p.city, p.state].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
};

const STATUS_FILTERS: { value: ProjectStatusFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

const Projects = () => {
  const pagination = usePagination();
  const [statusFilter, setStatusFilter] = useState<ProjectStatusFilter>("active");
  const { data: projects, isLoading } = useProjects(pagination, statusFilter);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; project_number?: string; name: string } | null>(null);
  const navigate = useTabNavigate();
  const { can } = usePermissions();
  const { doExport } = useExportActions({ entityType: "projects" });
  const deleteProject = useDeleteProject();

  // Reset to first page when changing filter
  useEffect(() => {
    pagination.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const hasData = projects && projects.length > 0;

  const columns = useMemo<ColumnDef<any, any>[]>(() => [
    {
      accessorKey: "project_number",
      header: "Project #",
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <Badge className="bg-foreground text-background font-mono text-xs">{v}</Badge> : "—";
      },
    },
    { accessorKey: "name", header: "Project Name", cell: ({ getValue }) => <span className="font-medium">{getValue()}</span> },
    { id: "type", header: "Type", accessorFn: (row: any) => row.is_personnel ? "Personnel" : (row.project_types?.name ?? "—"),
      cell: ({ row }: any) => row.original.is_personnel ? <Badge variant="outline" className="border-primary/30 text-primary text-xs">Personnel</Badge> : (row.original.project_types?.name ?? "—"),
    },
    { id: "customer", header: "Customer", accessorFn: (row: any) => row.customers?.name ?? "—" },
    { id: "location", header: "Location", accessorFn: (row: any) => locationStr(row) },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
      enableColumnFilter: true,
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ getValue }) => format(new Date(getValue()), "MMM d, yyyy"),
    },
  ], []);

  const pageActions = [
    { label: "New Project", icon: Plus, onClick: () => setDialogOpen(true), permission: can("edit_project_info") },
    { label: "Export to Excel", icon: FileSpreadsheet, onClick: () => hasData && doExport("excel", projects!), permission: !!hasData, separator: true },
    { label: "Export to CSV", icon: FileText, onClick: () => hasData && doExport("csv", projects!), permission: !!hasData },
    { label: "Print", icon: Printer, onClick: () => hasData && doExport("print", projects!), permission: !!hasData },
  ];

  return (
    <RequirePermission anyOf={["view_projects", "edit_project_info"]}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Projects</h1>
          <PageActions actions={pageActions} />
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              variant={statusFilter === f.value ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Mobile card view */}
        <div className="space-y-3 md:hidden">
          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : !projects?.length ? (
            <p className="text-muted-foreground">No projects yet.</p>
          ) : (
            projects.map((p) => (
              <Card key={p.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate(`/controller/projects/${p.id}`)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {p.project_number && <Badge className="bg-foreground text-background font-mono text-xs shrink-0">{p.project_number}</Badge>}
                        <StatusBadge status={p.status} />
                      </div>
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{p.customers?.name ?? "—"} · {locationStr(p)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Desktop DataTable */}
        <div className="hidden md:block">
          <DataTable
            tableId="projects-list"
            columns={columns}
            data={projects ?? []}
            isLoading={isLoading}
            emptyMessage="No projects yet."
            onRowClick={(p: any) => navigate(`/controller/projects/${p.id}`)}
          />
        </div>

        {hasData && <PaginationControls pagination={pagination} />}

        <NewProjectWizard open={dialogOpen} onOpenChange={setDialogOpen} />

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Project?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete project{" "}
                <span className="font-mono font-semibold">{deleteTarget?.project_number ?? deleteTarget?.name}</span>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteProject.isPending}
                onClick={async () => {
                  if (!deleteTarget) return;
                  try {
                    await deleteProject.mutateAsync(deleteTarget.id);
                    toast.success(`Project ${deleteTarget.project_number ?? deleteTarget.name} deleted`);
                    setDeleteTarget(null);
                  } catch (e: any) {
                    toast.error(e.message ?? "Failed to delete project");
                  }
                }}
              >
                {deleteProject.isPending ? "Deleting…" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </RequirePermission>
  );
};

export default Projects;
